/**
 * Terms of Service Page
 *
 * Displays the full Terms of Service content fetched from Firestore.
 * Features:
 * - Fetches current terms version from Firestore
 * - Renders markdown/text content
 * - Shows last updated date
 * - Print button for offline copy
 * - Error handling with retry
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getTermsContent, getCurrentTermsConfig } from '../services/termsService';

// Default terms content (fallback if Firestore unavailable)
const DEFAULT_TERMS = {
  title: 'Terms of Service',
  version: '1.0.0',
  effectiveDate: new Date(),
  content: `
# QuickMeet Terms of Service

**Last Updated: December 2025**

## 1. Acceptance of Terms

By accessing or using QuickMeet ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service.

## 2. Description of Service

QuickMeet is a video conferencing platform that allows users to:
- Host and join video meetings
- Chat with other users
- Share files and screens
- Record meetings (subject to tier limitations)

## 3. User Accounts

### 3.1 Registration
To use certain features, you must create an account with accurate and complete information.

### 3.2 Account Security
You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.

### 3.3 Account Termination
We may suspend or terminate your account for violations of these Terms or applicable laws.

## 4. User Conduct

You agree not to:
- Use the Service for illegal purposes
- Harass, abuse, or harm other users
- Transmit malware or harmful code
- Attempt to gain unauthorized access
- Interfere with the Service's operation
- Violate intellectual property rights

## 5. Privacy

Your use of the Service is subject to our Privacy Policy. By using the Service, you consent to the collection and use of your information as described therein.

## 6. Subscription Tiers

### 6.1 Free Tier
- Limited features with advertisements
- 720p video quality
- Limited recording storage

### 6.2 Pro Tier
- Ad-free experience
- 1080p video quality
- Enhanced features

### 6.3 Team Tier
- All Pro features
- Team management
- Organization features

## 7. Payment Terms

- Subscriptions are billed in advance
- Refunds are provided per our refund policy
- Prices may change with notice

## 8. Intellectual Property

- QuickMeet and its content are protected by copyright and trademark laws
- You retain ownership of content you create
- You grant us license to use your content to provide the Service

## 9. Disclaimers

THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.

## 10. Limitation of Liability

IN NO EVENT SHALL QUICKMEET BE LIABLE FOR INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES.

## 11. Changes to Terms

We may update these Terms. Continued use constitutes acceptance of updated Terms.

## 12. Contact

For questions about these Terms, contact us at support@quickmeet.cam.
  `.trim()
};

export default function Terms() {
  const navigate = useNavigate();
  const [termsContent, setTermsContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load terms content
  useEffect(() => {
    const loadTerms = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get current config
        const config = await getCurrentTermsConfig();

        // Get terms content for current version
        const content = await getTermsContent(config.currentVersion, 'terms');

        if (content) {
          setTermsContent(content);
        } else {
          // Use default if not found in Firestore
          setTermsContent(DEFAULT_TERMS);
        }
      } catch (err) {
        console.error('Error loading terms:', err);
        setError('Unable to load Terms of Service. Please try again.');
        // Fallback to default
        setTermsContent(DEFAULT_TERMS);
      } finally {
        setLoading(false);
      }
    };

    loadTerms();
  }, []);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#1c1c1e]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-[#1c1c1e]/80 backdrop-blur-xl border-b border-[#d2d2d7] dark:border-[#2c2c2e]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 text-[#1d1d1f] dark:text-white hover:opacity-70 transition-opacity"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-[#06c] to-[#0055d4] rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <span className="font-semibold text-lg">QuickMeet</span>
          </Link>

          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="p-2 text-[#6e6e73] hover:text-[#1d1d1f] dark:hover:text-white transition-colors"
              title="Print"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
            </button>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm font-medium text-[#06c] hover:text-[#0055d4] transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-10 h-10 border-2 border-[#06c]/30 border-t-[#06c] rounded-full animate-spin" />
          </div>
        ) : error && !termsContent ? (
          <div className="text-center py-24">
            <div className="w-20 h-20 bg-red-100 dark:bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-[#1d1d1f] dark:text-white mb-2">
              Unable to Load Terms
            </h2>
            <p className="text-[#6e6e73] dark:text-[#8e8e93] mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-[#06c] text-white rounded-xl hover:bg-[#0055d4] transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            {/* Title and Meta */}
            <div className="mb-12 text-center">
              <h1 className="text-4xl font-semibold text-[#1d1d1f] dark:text-white mb-4">
                {termsContent?.title || 'Terms of Service'}
              </h1>
              <p className="text-[#6e6e73] dark:text-[#8e8e93]">
                Version {termsContent?.version || '1.0.0'} - Last updated{' '}
                {termsContent?.effectiveDate
                  ? new Date(termsContent.effectiveDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  : 'December 2025'}
              </p>
            </div>

            {/* Terms Content */}
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <div className="bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl p-8 whitespace-pre-wrap text-[#1d1d1f] dark:text-[#e5e5ea] leading-relaxed">
                {termsContent?.content || DEFAULT_TERMS.content}
              </div>
            </div>

            {/* Footer Links */}
            <div className="mt-12 pt-8 border-t border-[#d2d2d7] dark:border-[#2c2c2e] flex flex-wrap gap-6 justify-center text-sm">
              <Link to="/privacy" className="text-[#06c] hover:underline">
                Privacy Policy
              </Link>
              <Link to="/about" className="text-[#06c] hover:underline">
                About QuickMeet
              </Link>
              <Link to="/security" className="text-[#06c] hover:underline">
                Security
              </Link>
              <a href="mailto:support@quickmeet.cam" className="text-[#06c] hover:underline">
                Contact Support
              </a>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#d2d2d7] dark:border-[#2c2c2e] py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-[#6e6e73] dark:text-[#8e8e93]">
          <p>© 2025 TheJhyeFactor. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
